import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Marker as MLMarker, Popup } from 'react-map-gl';
import { Marker } from 'react-leaflet';
import { FEATURE_FLAGS } from '#/shared/constants';
import { locationType } from '#/prop-types';
import useComponentAsLeafletIcon from '#/map/hooks/useComponentAsLeafletIcon';
import MarkerTooltip from '#/map/components/MarkerTooltip';
import { CurrentLocationMarker } from './styled';

const { MAPLIBRE_MODE } = FEATURE_FLAGS;

const CurrentLocationOverlay = forwardRef(
  (
    {
      currentLocation,
      zIndexOffset,
      markerSize,
      onDragEnd,
      isMobile,
      isManualLocation,
    },
    ref
  ) => {
    const [showPopup, setShowPopup] = useState(false);
    const markerRadius = markerSize / 2;
    const markerIcon = useComponentAsLeafletIcon(
      <CurrentLocationMarker size={markerSize} pulsate={!isManualLocation} />,
      { iconAnchor: [markerRadius, markerRadius] }
    );

    const handleMouseEnter = () => setShowPopup(true);
    const handleMouseLeave = () => setShowPopup(false);

    if (MAPLIBRE_MODE) {
      return (
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <MLMarker
            ref={ref}
            latitude={currentLocation.lat}
            longitude={currentLocation.lon}
            draggable={!isMobile && isManualLocation}
            onDragEnd={onDragEnd}
          >
            {showPopup && (
              <Popup
                latitude={currentLocation.lat}
                longitude={currentLocation.lon}
                offset={10}
                closeButton={false}
              >
                {currentLocation.name}
              </Popup>
            )}
            <CurrentLocationMarker
              size={markerSize}
              pulsate={!isManualLocation}
            />
          </MLMarker>
        </div>
      );
    }

    return (
      <Marker
        ref={ref}
        draggable={!isMobile && isManualLocation}
        onDragend={onDragEnd}
        class="marker"
        position={[currentLocation.lat, currentLocation.lon]}
        zIndexOffset={zIndexOffset}
        keyboard={false}
        icon={markerIcon}
      >
        <MarkerTooltip offset={[0, -markerRadius]}>
          {currentLocation.name}
        </MarkerTooltip>
      </Marker>
    );
  }
);

// the component's display name must be set explicitly because it is wrapped in
// `forwardRef`: https://github.com/yannickcr/eslint-plugin-react/issues/2269
CurrentLocationOverlay.displayName = 'CurrentLocationOverlay';

CurrentLocationOverlay.propTypes = {
  /** Represents the user's current location */
  currentLocation: locationType.isRequired,
  /** Pass through to prop of same name on Leaflet `Marker` component */
  zIndexOffset: PropTypes.number,
  /** Size of marker in pixels as a number */
  markerSize: PropTypes.number,
  /** Handler for Leaflet Marker's `dragend` event */
  onDragEnd: PropTypes.func,
  /** Boolean to whether or not the user is using a mobile device */
  isMobile: PropTypes.bool,
  /** Is false whether or not current location is set manually on the map */
  isManualLocation: PropTypes.bool,
};

CurrentLocationOverlay.defaultProps = {
  zIndexOffset: 0,
  markerSize: 16,
  onDragEnd: () => {},
  isMobile: null,
  isManualLocation: false,
};

export default CurrentLocationOverlay;
export { CurrentLocationMarker };
